import { Component, inject, Input, OnInit } from '@angular/core';
import { OptionsList } from 'gung-common';
import { GungCompany } from '../../../services/gung-company/gung-company.service';
import { GungNavbarService } from '../../../services/gung-navbar/gung-navbar.service';
import { CheckoutConfigService } from '../../../services/checkout-config/checkout-config.service';
import { CartListCheckoutConfigService } from '../../../services/cart-list-checkout-config/cart-list-checkout-config.service';
import { ProductListSettingsService } from '../../../services/product-list-settings/product-list-settings.service';
import { BaseViewConfigService, ProductViewType } from '../../../services/base-view-config/base-view-config.service';
import { ProductListConfigService } from '../../../services/product-list-config/product-list-config.service';
import { ProductListV2ConfigService } from '../../../services/product-list-v2-config/product-list-v2-config.service';

export type NavbarType = 'assortment' | 'flow';

export interface MyCompanyLayoutNavbar {
  type: NavbarType;
  assortmentMenuLevel?: number | 'auto';
  removeUserAssortmentTopLevel?: boolean;
  forceToNotShowFlowsOnTop?: boolean;
  hideMyCustomers?: boolean;
  hideFavorites?: boolean;
  hideCart?: boolean;
  hideSelectedCustomer?: boolean;
  showNavbarSearch?: boolean;
}

export interface MyCompanyLayoutCheckout {
  sideButtons?: boolean;
  enableDiscount?: boolean;
  enableOffers?: boolean;
  hideCreateQuotation?: boolean;
  enableGlobalDeliveryDate?: boolean;
  enableSalesEditPrice?: boolean;
  enableSalesEditDiscount?: boolean;
  userCanAddDescription?: boolean;
  enableCopyRow?: boolean;
  userCanChangeDeliveryDates?: boolean;
  enableOrderOfferStats?: boolean;
}

export interface MyCompanyLayoutProductList {
  hideUndefinedFilters?: boolean;
  topFilter?: boolean;
  batchSizes?: number[];
  displayProductsOnVariantLevel?: boolean;
  fetchProductsExpanded?: boolean;
  searchDisabled?: boolean;
  assortmentBannerEnabled?: boolean;
  keepFilterOpen?: boolean;
  infiniteScroll?: boolean;
  showProductListInArticlesView?: boolean;
}

export interface MyCompanyLayout {
  maxWidth: number;
  excludedUrls: string[];
  navbar?: MyCompanyLayoutNavbar | null;
  checkout?: MyCompanyLayoutCheckout | null;
  productList?: MyCompanyLayoutProductList | null;
}

@Component({
  selector: 'lib-my-company-layout',
  templateUrl: './my-company-layout.component.html',
  styleUrls: ['./my-company-layout.component.css'],
  standalone: false
})
export class MyCompanyLayoutComponent implements OnInit {
  protected gungNavbarService = inject(GungNavbarService);
  protected checkoutConfigService = inject(CheckoutConfigService);
  protected cartListCheckoutConfigService = inject(CartListCheckoutConfigService);
  protected productListSettingsService = inject(ProductListSettingsService);
  protected baseViewConfigService = inject(BaseViewConfigService);
  protected productListConfigService = inject(ProductListConfigService);
  protected productListV2ConfigService = inject(ProductListV2ConfigService);

  @Input()
  public company: GungCompany;

  public myCompanyLayout: MyCompanyLayout;

  isAssortmentTreeView = this.baseViewConfigService.productViewType === ProductViewType.assortmentTreeView;
  usingProductListV2 = this.baseViewConfigService.productViewType === ProductViewType.productV2;
  productListModeLabel = this.isAssortmentTreeView
    ? 'Assortment Tree View'
    : this.usingProductListV2
      ? 'Product List V2'
      : 'Product List (default view)';

  showingCodeDefaults = false;
  showingCheckoutCodeDefaults = false;
  showingProductListCodeDefaults = false;
  newUrl = '';

  navbarTypeOptions: OptionsList[] = [
    { id: 'assortment', name: 'Assortment' },
    { id: 'flow', name: 'Flow' }
  ];

  assortmentMenuLevelOptions: OptionsList[] = [
    { id: '0', name: '0' },
    { id: '1', name: '1' },
    { id: '2', name: '2' },
    { id: '3', name: '3' },
    { id: 'auto', name: 'auto' }
  ];

  get assortmentMenuLevelStr(): string {
    return String(this.myCompanyLayout?.navbar?.assortmentMenuLevel ?? '0');
  }

  set assortmentMenuLevelStr(val: string) {
    if (this.myCompanyLayout?.navbar) {
      this.myCompanyLayout.navbar.assortmentMenuLevel = val === 'auto' ? 'auto' : Number(val);
    }
  }

  get batchSizesStr(): string {
    return this.myCompanyLayout?.productList?.batchSizes?.join(', ') ?? '';
  }

  set batchSizesStr(val: string) {
    if (!this.myCompanyLayout?.productList) {
      return;
    }
    const parsed = (val || '')
      .split(',')
      .map(part => Number(part.trim()))
      .filter(n => !isNaN(n) && n > 0);
    this.myCompanyLayout.productList.batchSizes = parsed.length ? parsed : undefined;
  }

  ngOnInit(): void {
    if (!this.company.extra) {
      this.company.extra = {};
    }
    if (!this.company.extra.layout) {
      this.company.extra.layout = { maxWidth: null, excludedUrls: [], navbar: null };
    }
    this.myCompanyLayout = this.company.extra.layout;
    if (!this.myCompanyLayout.navbar) {
      this.myCompanyLayout.navbar = { type: this.gungNavbarService.isAssortmentBased ? 'assortment' : 'flow' };
    }
    this.applyNavbarDefaults(this.myCompanyLayout.navbar);
    if (!this.myCompanyLayout.checkout) {
      this.myCompanyLayout.checkout = {};
    }
    this.applyCheckoutDefaults(this.myCompanyLayout.checkout);
    if (!this.myCompanyLayout.productList) {
      this.myCompanyLayout.productList = {};
    }
    this.applyProductListDefaults(this.myCompanyLayout.productList);
  }

  protected applyNavbarDefaults(navbar: MyCompanyLayoutNavbar): void {
    const svc = this.gungNavbarService;
    if (navbar.type == null) {
      navbar.type = svc.isAssortmentBased ? 'assortment' : 'flow';
    }
    const level = navbar.assortmentMenuLevel;
    if (level == null) {
      navbar.assortmentMenuLevel = svc.assortmentMenuLevel;
    } else if (level !== 'auto') {
      navbar.assortmentMenuLevel = Number(level);
    }
    if (navbar.removeUserAssortmentTopLevel == null) {
      navbar.removeUserAssortmentTopLevel = svc.removeUserAssortmentTopLevel;
    }
    if (navbar.forceToNotShowFlowsOnTop == null) {
      navbar.forceToNotShowFlowsOnTop = svc.forceToNotShowFlowsOnTop;
    }
    if (navbar.hideMyCustomers == null) {
      navbar.hideMyCustomers = svc.hideMyCustomers;
    }
    if (navbar.hideFavorites == null) {
      navbar.hideFavorites = svc.hideFavorites;
    }
    if (navbar.hideCart == null) {
      navbar.hideCart = svc.hideCart;
    }
    if (navbar.hideSelectedCustomer == null) {
      navbar.hideSelectedCustomer = svc.hideSelectedCustomer;
    }
    if (navbar.showNavbarSearch == null) {
      navbar.showNavbarSearch = svc.showNavbarSearch;
    }
  }

  protected applyCheckoutDefaults(checkout: MyCompanyLayoutCheckout): void {
    const svc = this.checkoutConfigService;
    if (checkout.sideButtons == null) {
      checkout.sideButtons = svc.sideButtons;
    }
    if (checkout.enableDiscount == null) {
      checkout.enableDiscount = svc.enableDiscount;
    }
    if (checkout.enableOffers == null) {
      checkout.enableOffers = svc.enableOffers;
    }
    if (checkout.hideCreateQuotation == null) {
      checkout.hideCreateQuotation = svc.hideCreateQuotation;
    }
    if (checkout.enableGlobalDeliveryDate == null) {
      checkout.enableGlobalDeliveryDate = this.cartListCheckoutConfigService.enableGlobalDeliveryDate;
    }
    if (checkout.enableSalesEditPrice == null) {
      checkout.enableSalesEditPrice = this.cartListCheckoutConfigService.enableSalesEditPrice;
    }
    if (checkout.enableSalesEditDiscount == null) {
      checkout.enableSalesEditDiscount = this.cartListCheckoutConfigService.enableSalesEditDiscount;
    }
    if (checkout.userCanAddDescription == null) {
      checkout.userCanAddDescription = this.cartListCheckoutConfigService.userCanAddDescription;
    }
    if (checkout.enableCopyRow == null) {
      checkout.enableCopyRow = this.cartListCheckoutConfigService.enableCopyRow;
    }
    if (checkout.userCanChangeDeliveryDates == null) {
      checkout.userCanChangeDeliveryDates = this.cartListCheckoutConfigService.userCanChangeDeliveryDates;
    }
    if (checkout.enableOrderOfferStats == null) {
      checkout.enableOrderOfferStats = this.cartListCheckoutConfigService.enableOrderOfferStats;
    }
  }

  protected applyProductListDefaults(productList: MyCompanyLayoutProductList): void {
    if (this.isAssortmentTreeView) {
      if (productList.showProductListInArticlesView == null) {
        productList.showProductListInArticlesView = this.baseViewConfigService.showProductListInArticlesView();
      }
      if (!productList.showProductListInArticlesView) {
        // /articles shows only the assortment tree here, no product list is rendered, so none
        // of the settings below have anything to apply to yet.
        return;
      }
    }

    if (productList.hideUndefinedFilters == null) {
      productList.hideUndefinedFilters = this.productListSettingsService.hideUndefinedFilters;
    }

    // Once a product list is actually shown in assortmentTreeView, it's backed by
    // ProductListV2ConfigService (see AssortmentTreeListComponent), so treat it like v2 here too.
    const useV2Fields = this.usingProductListV2 || this.isAssortmentTreeView;

    if (productList.topFilter == null) {
      productList.topFilter = useV2Fields ? this.productListV2ConfigService.topFilter : this.productListConfigService.topFilter;
    }
    if (productList.batchSizes == null) {
      productList.batchSizes = useV2Fields
        ? this.productListV2ConfigService.getBatchSizes()
        : this.productListConfigService.getBatchSizes();
    }
    if (productList.infiniteScroll == null) {
      productList.infiniteScroll = this.baseViewConfigService.productListInfiniteScroll;
    }

    if (!useV2Fields) {
      if (productList.displayProductsOnVariantLevel == null) {
        productList.displayProductsOnVariantLevel = this.productListConfigService.displayProductsOnVariantLevel;
      }
      if (productList.keepFilterOpen == null) {
        productList.keepFilterOpen = this.productListConfigService.keepFilterOpen;
      }
    } else {
      if (productList.fetchProductsExpanded == null) {
        productList.fetchProductsExpanded = this.productListV2ConfigService.fetchProductsExpanded;
      }
      if (productList.searchDisabled == null) {
        productList.searchDisabled = this.productListV2ConfigService.searchDisabled;
      }
      if (productList.assortmentBannerEnabled == null) {
        productList.assortmentBannerEnabled = this.productListV2ConfigService.assortmentBannerEnabled;
      }
    }
  }

  addUrl() {
    const url = this.newUrl?.trim();
    if (url && !this.myCompanyLayout.excludedUrls.includes(url)) {
      this.myCompanyLayout.excludedUrls.push(url);
    }
    this.newUrl = '';
  }

  removeUrl(index: number) {
    this.myCompanyLayout.excludedUrls.splice(index, 1);
  }

  resetNavbarToDefaults() {
    this.myCompanyLayout.navbar = null;
    this.showingCodeDefaults = true;
  }

  customizeNavbar() {
    this.myCompanyLayout.navbar = { type: this.gungNavbarService.isAssortmentBased ? 'assortment' : 'flow' };
    this.applyNavbarDefaults(this.myCompanyLayout.navbar);
    this.showingCodeDefaults = false;
  }

  resetCheckoutToDefaults() {
    this.myCompanyLayout.checkout = null;
    this.showingCheckoutCodeDefaults = true;
  }

  customizeCheckout() {
    this.myCompanyLayout.checkout = {};
    this.applyCheckoutDefaults(this.myCompanyLayout.checkout);
    this.showingCheckoutCodeDefaults = false;
  }

  resetProductListToDefaults() {
    this.myCompanyLayout.productList = null;
    this.showingProductListCodeDefaults = true;
  }

  customizeProductList() {
    this.myCompanyLayout.productList = {};
    this.applyProductListDefaults(this.myCompanyLayout.productList);
    this.showingProductListCodeDefaults = false;
  }
}
